import { useState, useEffect } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { TreeVisualization } from '@/components/customer/TreeVisualization'
import { CityVisualization } from '@/components/customer/CityVisualization'
import { SocialMilestoneNotification } from '@/components/customer/SocialMilestoneNotification'
import { SocialSharingButton } from '@/components/customer/SocialSharingButton'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/hooks/use-toast'
import { Heart, Info, TreePine, Users, GraduationCap } from 'lucide-react'

interface CustomerContribution {
  id: string
  contribution_count: number
  last_contribution_at: string | null
  social_project: {
    id: string
    name: string
    description: string | null
    project_type: 'trees' | 'families' | 'education'
  }
}

interface TenantContributionSummary {
  social_project_id: string
  total_contributions: number
  project_type: string
  project_name: string
}

const CustomerSocialEngagement = () => {
  const { user } = useAuth()
  const { toast } = useToast()
  const [customerContributions, setCustomerContributions] = useState<CustomerContribution[]>([])
  const [tenantSummaries, setTenantSummaries] = useState<TenantContributionSummary[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user?.id) {
      fetchContributions()
    }
  }, [user?.id])

  const fetchContributions = async () => {
    if (!user?.id) return

    try {
      // Get customer data using secure RPC
      const { data: customerData, error: customerError } = await supabase.rpc('get_or_create_customer_profile', {
        p_user_id: user.id,
        p_email: user.email || '',
        p_full_name: user.user_metadata?.full_name || 'Kunde'
      })

      if (customerError || !customerData || customerData.length === 0) {
        console.error('Error fetching customer data:', customerError)
        return
      }

      const customer = customerData[0]

      // Fetch customer's personal contributions
      const { data: contributions, error: contributionsError } = await supabase
        .from('customer_social_contributions')
        .select(`
          *,
          social_project:social_projects (
            id,
            name,
            description,
            project_type
          )
        `)
        .eq('customer_id', customer.id)
        .gt('contribution_count', 0)

      if (contributionsError) throw contributionsError

      // Fetch tenant-wide summaries for enabled projects
      const { data: tenantProjects, error: tenantProjectsError } = await supabase
        .from('tenant_social_projects')
        .select(`
          social_project_id,
          social_project:social_projects (
            name,
            project_type
          )
        `)
        .eq('tenant_id', customer.tenant_id)
        .eq('is_enabled', true)

      if (tenantProjectsError) throw tenantProjectsError

      // For each enabled project, get total contributions across all customers in the same tenant
      const summaries: TenantContributionSummary[] = []
      
      for (const tenantProject of tenantProjects || []) {
        // Use a raw query with proper join to get contributions from all customers in the same tenant
        const { data: totalContributions, error: totalError } = await (supabase as any)
          .rpc('get_tenant_social_contributions', {
            p_tenant_id: customer.tenant_id,
            p_social_project_id: tenantProject.social_project_id
          })

        if (totalError) {
          console.error('Error fetching total contributions:', totalError)
          continue
        }

        const total = typeof totalContributions === 'number' ? totalContributions : 0

        summaries.push({
          social_project_id: tenantProject.social_project_id,
          total_contributions: total,
          project_type: tenantProject.social_project.project_type,
          project_name: tenantProject.social_project.name
        })
      }

      setCustomerContributions(contributions as CustomerContribution[] || [])
      setTenantSummaries(summaries)
    } catch (error) {
      console.error('Error fetching contributions:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Beiträge konnten nicht geladen werden.'
      })
    } finally {
      setLoading(false)
    }
  }

  const getProjectIcon = (type: string) => {
    switch (type) {
      case 'trees': return TreePine;
      case 'families': return Heart;
      case 'education': return GraduationCap;
      default: return Heart;
    }
  }

  const getUserContribution = (projectId: string) => {
    const contribution = customerContributions.find(c => c.social_project.id === projectId)
    return contribution?.contribution_count || 0
  }

  return (
    <DashboardLayout>
      <SocialMilestoneNotification />
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground">Lade soziale Projekte...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold tracking-tight flex items-center justify-center gap-2">
              <Heart className="h-8 w-8 text-red-500" />
              Soziales Engagement
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Durch Ihre Empfehlungen unterstützen Sie automatisch soziale Projekte. 
              Jede erfolgreiche Empfehlung trägt zu einer besseren Welt bei.
            </p>
          </div>

          {tenantSummaries.length === 0 ? (
            <Card>
              <CardContent className="p-6">
                <div className="text-center py-8 text-muted-foreground">
                  <Heart className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-semibold mb-2">Keine sozialen Projekte aktiv</h3>
                  <p className="text-sm">
                    Derzeit sind keine sozialen Projekte in Ihrem Unternehmen aktiviert.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Info Card */}
              <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-background">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-primary">
                    <Info className="h-5 w-5" />
                    Ihr sozialer Beitrag
                  </CardTitle>
                  <CardDescription>
                    Für jede erfolgreiche Empfehlung leisten Sie automatisch einen Beitrag zu den 
                    aktivierten sozialen Projekten. Ihre goldenen Markierungen zeigen Ihren 
                    persönlichen Beitrag.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Project Visualizations */}
              <div className="grid gap-6 lg:grid-cols-1 xl:grid-cols-2">
                {tenantSummaries.map((summary) => {
                  const userContribution = getUserContribution(summary.social_project_id)
                  
                  return (
                    <div key={summary.social_project_id}>
                      {summary.project_type === 'trees' && (
                        <TreeVisualization
                          totalTrees={summary.total_contributions}
                          userTrees={userContribution}
                          projectName={summary.project_name}
                        />
                      )}
                      
                      {(summary.project_type === 'families' || summary.project_type === 'family_help') && (
                        <CityVisualization
                          totalHouses={summary.total_contributions}
                          userHouses={userContribution}
                          projectName={summary.project_name}
                        />
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Eigene Beiträge mit Sharing */}
              {customerContributions.length > 0 && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-center">Ihr Engagement im Detail</h2>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {customerContributions.map((contribution) => {
                      const Icon = getProjectIcon(contribution.social_project.project_type);
                      return (
                        <Card key={contribution.id} className="text-center">
                          <CardHeader className="pb-3">
                            <div className="flex justify-center mb-2">
                              <Icon className="h-8 w-8 text-primary" />
                            </div>
                            <CardTitle className="text-lg">{contribution.social_project.name}</CardTitle>
                            <CardDescription>
                              {contribution.social_project.description}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="text-3xl font-bold text-primary mb-2">
                              {contribution.contribution_count}
                            </div>
                            <Badge variant="secondary" className="mb-4">
                              {contribution.social_project.project_type === 'trees' ? 'Bäume' : 
                               contribution.social_project.project_type === 'families' ? 'Familien unterstützt' :
                               'Projekte unterstützt'}
                            </Badge>
                            {contribution.last_contribution_at && (
                              <p className="text-xs text-muted-foreground mb-4">
                                Letzter Beitrag: {new Date(contribution.last_contribution_at).toLocaleDateString('de-DE')}
                              </p>
                            )}
                            <SocialSharingButton 
                              projectName={contribution.social_project.name}
                              contributionCount={contribution.contribution_count}
                              projectType={contribution.social_project.project_type}
                              className="w-full"
                            />
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {customerContributions.length === 0 && (
                <Card>
                  <CardContent className="p-6">
                    <div className="text-center py-8 text-muted-foreground">
                      <Heart className="h-12 w-12 mx-auto mb-4 text-primary" />
                      <h3 className="font-semibold mb-2">Noch keine Beiträge</h3>
                      <p className="text-sm">
                        Sobald Sie erfolgreich jemanden empfohlen haben, sehen Sie hier Ihren Beitrag 
                        zu den sozialen Projekten.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}
    </DashboardLayout>
  )
}

export default CustomerSocialEngagement